import React, { useState, useEffect } from 'react';
import "../pages/css/DashBoard.css";
import Header from '../pages/components/HeaderUser';
import SideBar from '../pages/components/SidebarUser';
import UserVerificationUser from '../pages/components/UserVerificationUser';
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaSpinner } from "react-icons/fa";
import Swal from 'sweetalert2';

function UserDashBoard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [buyedCoins, setBuyedCoins] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);

  const auth = getAuth();
  const db = getDatabase();
  const navigate = useNavigate();

  const Loader = () => (
    <div style={{
      height: '100vh',
      width: '100%',
      backgroundColor: '#fff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      position: 'fixed',
      top: 0,
      left: 0
    }}>
      <FaSpinner style={{
        fontSize: '3rem',
        color: '#275E76',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  UserVerificationUser();
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  // ✅ Listen to auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        setUid(null);
        setBuyedCoins({});
        setWalletBalance(0);
        setTotalSpent(0);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [auth]);

  // Fetch user data after UID is available
  useEffect(() => {
    if (!uid) return;

    const fetchData = async () => {
      try {
        // Buyed Coins
        const coinsSnap = await get(ref(db, `blackstone-users/${uid}/buyedCoins`));
        if (coinsSnap.exists()) setBuyedCoins(coinsSnap.val());

        // Wallet Balance
        const walletSnap = await get(ref(db, `blackstone-users/${uid}/walletBalance`));
        if (walletSnap.exists()) setWalletBalance(Number(walletSnap.val()) || 0);

        // Total Spent
        const spentSnap = await get(ref(db, `blackstone-users/${uid}/rechargeDetails`));
        if (spentSnap.exists()) {
          let sum = 0;
          Object.values(spentSnap.val()).forEach(detail => {
            sum += detail.amount || 0;
          });
          setTotalSpent(sum);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uid, db]);

  // Prepare coin boxes (3 per row)
  const orderedCoins = [];
  ["bitcoin", "ethereum", "tether"].forEach(c => {
    if (buyedCoins[c]) {
      orderedCoins.push({
        color:
          c === "bitcoin"
            ? "linear-gradient(135deg, #667eea, #764ba2)"
            : c === "ethereum"
              ? "linear-gradient(135deg, #ff6a00, #ee0979)"
              : "linear-gradient(135deg, #56ccf2, #2f80ed)",
        title: c.toUpperCase(),
        number: buyedCoins[c],
        link: "/add-coins"
      });
    }
  });
  Object.keys(buyedCoins).forEach(c => {
    if (!["bitcoin", "ethereum", "tether"].includes(c)) {
      orderedCoins.push({
        color: "linear-gradient(135deg, #11998e, #38ef7d)",
        title: c.toUpperCase(),
        number: buyedCoins[c],
        link: "/add-coins"
      });
    }
  });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Loader />
      </div>
    );
  }

  return (
    <div className="d-flex layout-container">
      <SideBar isOpen={isSidebarOpen} />
      <div className='flex-grow-1'>
        <Header onMenuClick={toggleSidebar} />
        <div className="scrollable-section p-4">
          {/* --- Coins Row --- */}
          <div className="row g-4 mb-4">
            {orderedCoins.map((box, idx) => (
              <div className="col-md-4" key={idx}>
                <div
                  className="d-flex flex-column justify-content-center align-items-center text-white p-4"
                  style={{
                    background: box.color,
                    borderRadius: "20px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    height: "200px",
                    textAlign: "center",
                  }}
                >
                  <h2>{box.number.toFixed(6)}</h2>
                  <h5>{box.title}</h5>
                  <button
                    className="mt-2 px-4 py-2 text-white"
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.7)",
                      borderRadius: "12px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                    }}
                    onClick={() => navigate(box.link)}
                  >
                    Buy More
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* --- Wallet Row --- */}
          <div className="row g-4">
            <div className="col-md-4">
              <div
                className="d-flex flex-column justify-content-center align-items-center text-white p-4"
                style={{
                  background: "linear-gradient(135deg, #11998e, #38ef7d)",
                  borderRadius: "20px",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  height: "200px",
                  textAlign: "center",
                }}
              >
                <h2>₹{walletBalance.toLocaleString("en-IN")}</h2>
                <h5>Wallet Balance</h5>
                <button
                  className="mt-2 px-4 py-2 text-white"
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.7)",
                    borderRadius: "12px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  }}
                  onClick={() => navigate("/add-funds")}
                >
                  Recharge Wallet
                </button>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="d-flex flex-column justify-content-center align-items-center text-white p-4"
                style={{
                  background: "linear-gradient(135deg, #f7971e, #ffd200)",
                  borderRadius: "20px",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  height: "200px",
                  textAlign: "center",
                }}
              >
                <h2>₹{totalSpent.toLocaleString("en-IN")}</h2>
                <h5>Total Spent</h5>
                <p>All deposits sum</p>
              </div>
            </div>

            <div className="col-md-4">
              <div
                className="d-flex flex-column justify-content-center align-items-center text-white p-4"
                style={{
                  background: "linear-gradient(135deg, #ff416c, #ff4b2b)",
                  borderRadius: "20px",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  height: "200px",
                  textAlign: "center",
                }}
              >
                {/* <h2>🔗</h2> */}
                <h3>Generate Referral Link</h3>
                <p>Click the button below</p>
                <button
                  className="mt-2 px-4 py-2 text-white"
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.7)",
                    borderRadius: "12px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  }}
                  onClick={() => {
                    const referralLink = `${window.location.origin}/register?ref=${uid}`;
                    navigator.clipboard.writeText(referralLink).then(() => {
                      Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Referral link copied to clipboard!',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true
                      });
                    }).catch(err => console.error("Clipboard copy failed:", err));
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}

export default UserDashBoard;
